import { createApi } from '@reduxjs/toolkit/dist/query/react'
import { getBaseQueryAuth } from 'helpers/rtkQueryHelpers'
import {
  BankDetailsRequest,
  BankDetailsResponse,
  BankDetailsListResponse,
  CompanyRegistrationRequest,
  CompanyRegistrationResponse,
  CompanyProfileUpdateRequest,
  SuccessResponse,
  CompanyOrderListResponse,
  CompanyOrder,
  CompanyOrderCreate,
  OrderRequest,
  OrderCreateResponse,
  SendMagicLinkRequest,
  SendMagicLinkResponse,
  MagicLinkLoginResponse,
  SendCompanyCodeRequest,
  SendCompanyCodeResponse,
  VerifyCompanyCodeRequest,
  VerifyCompanyCodeResponse,
} from '../types'

/**
 * RTK Query API для работы с компаниями (юридическими лицами)
 * Включает endpoints для:
 * - Регистрации компаний
 * - Управления банковскими реквизитами
 * - Работы с заявками компаний
 */

export const companyApi = createApi({
  reducerPath: 'companyApi',
  baseQuery: getBaseQueryAuth,
  tagTypes: ['BankDetails', 'CompanyOrder', 'CompanyOrders', 'CompanyProfile'],
  endpoints: builder => ({
    // === MAGIC LINK АУТЕНТИФИКАЦИЯ ===

    /**
     * Отправить magic link на email ЮЛ
     * POST /v1/company/email/send-link
     */
    uploadCustomerDocument: builder.mutation<
      { uid: string },
      { file: File; type: string }
    >({
      query: ({ file, type }) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('type', type)
        return {
          url: '/v1/customer/document/upload',
          method: 'POST',
          body: formData,
        }
      },
    }),

    sendMagicLink: builder.mutation<
      SendMagicLinkResponse,
      SendMagicLinkRequest
    >({
      query: body => ({
        url: '/v1/company/email/send-link',
        method: 'POST',
        body,
      }),
    }),

    /**
     * Вход по magic link токену — получение JWT
     * GET /v1/company/email/login/{token}
     */
    loginByMagicLink: builder.query<MagicLinkLoginResponse, string>({
      query: token => ({
        url: `/v1/company/email/login/${token}`,
        method: 'GET',
      }),
    }),

    // === SMS-ВЕРИФИКАЦИЯ ТЕЛЕФОНА ЮЛ ===

    /**
     * Отправить SMS-код на телефон ЮЛ
     * POST /v1/company/send-code
     */
    sendCompanyCode: builder.mutation<
      SendCompanyCodeResponse,
      SendCompanyCodeRequest
    >({
      query: body => ({
        url: '/v1/company/send-code',
        method: 'POST',
        body,
      }),
    }),

    /**
     * Подтвердить SMS-код ЮЛ
     * POST /v1/company/verify-code
     */
    verifyCompanyCode: builder.mutation<
      VerifyCompanyCodeResponse,
      VerifyCompanyCodeRequest
    >({
      query: body => ({
        url: '/v1/company/verify-code',
        method: 'POST',
        body,
      }),
    }),

    // === РЕГИСТРАЦИЯ КОМПАНИИ ===

    /**
     * Регистрация юридического лица
     * POST /v1/company/registration
     */
    registerCompany: builder.mutation<
      CompanyRegistrationResponse,
      CompanyRegistrationRequest
    >({
      query: body => ({
        url: '/v1/company/registration',
        method: 'POST',
        body,
      }),
    }),

    // === ПРОФИЛЬ КОМПАНИИ ===

    /**
     * Обновить профиль компании
     * PATCH /v1/company/profile
     */
    updateCompanyProfile: builder.mutation<
      SuccessResponse,
      CompanyProfileUpdateRequest
    >({
      query: body => ({
        url: '/v1/company/profile',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['CompanyProfile'],
    }),

    // === БАНКОВСКИЕ РЕКВИЗИТЫ ===

    /**
     * Получить список банковских реквизитов
     * GET /v1/company/bank-details
     */
    getBankDetailsList: builder.query<BankDetailsListResponse, void>({
      query: () => ({
        url: '/v1/company/bank-details',
        method: 'GET',
      }),
      providesTags: ['BankDetails'],
    }),

    /**
     * Добавить банковские реквизиты
     * POST /v1/company/bank-details
     */
    addBankDetails: builder.mutation<BankDetailsResponse, BankDetailsRequest>({
      query: body => ({
        url: '/v1/company/bank-details',
        method: 'POST',
        body: { ...body, isDefault: true },
      }),
      invalidatesTags: ['BankDetails'],
    }),

    /**
     * Обновить банковские реквизиты
     * PUT /v1/company/bank-details/{id}
     */
    updateBankDetails: builder.mutation<
      BankDetailsResponse,
      { id: number; data: BankDetailsRequest }
    >({
      query: ({ id, data }) => ({
        url: `/v1/company/bank-details/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['BankDetails'],
    }),

    /**
     * Удалить банковские реквизиты
     * DELETE /v1/company/bank-details/{id}
     */
    deleteBankDetails: builder.mutation<SuccessResponse, number>({
      query: id => ({
        url: `/v1/company/bank-details/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['BankDetails'],
    }),

    // === ЗАЯВКИ КОМПАНИИ ===

    /**
     * Получить список заявок компании
     * GET /v1/company/order/list
     */
    getCompanyOrdersList: builder.query<CompanyOrderListResponse, void>({
      query: () => ({
        url: '/v1/company/order/list',
        method: 'GET',
      }),
      providesTags: ['CompanyOrders'],
    }),

    /**
     * Проверить существование заявок компании
     * GET /v1/company/order/existing/list
     */
    checkCompanyOrdersExist: builder.query<{ hasOrders: boolean }, void>({
      query: () => ({
        url: '/v1/company/order/existing/list',
        method: 'GET',
      }),
    }),

    /**
     * Получить заявку компании
     * GET /v1/company/order
     */
    getCompanyOrder: builder.query<CompanyOrder, OrderRequest>({
      query: params => ({
        url: '/v1/company/order',
        method: 'GET',
        params,
      }),
      providesTags: (result, error, arg) => [
        {
          type: 'CompanyOrder',
          id: arg.uid || arg.orderId || undefined,
        },
      ],
    }),

    /**
     * Создать заявку компании
     * POST /v1/company/orders
     */
    createCompanyOrder: builder.mutation<
      OrderCreateResponse,
      CompanyOrderCreate
    >({
      query: body => ({
        url: '/v1/company/orders',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CompanyOrders'],
    }),
  }),
})

// Экспорт hooks для использования в компонентах
export const {
  // Document upload
  useUploadCustomerDocumentMutation,

  // Magic link
  useSendMagicLinkMutation,
  useLazyLoginByMagicLinkQuery,

  // SMS-верификация ЮЛ
  useSendCompanyCodeMutation,
  useVerifyCompanyCodeMutation,

  // Регистрация
  useRegisterCompanyMutation,

  // Профиль компании
  useUpdateCompanyProfileMutation,

  // Банковские реквизиты
  useGetBankDetailsListQuery,
  useAddBankDetailsMutation,
  useUpdateBankDetailsMutation,
  useDeleteBankDetailsMutation,

  // Заявки
  useGetCompanyOrdersListQuery,
  useCheckCompanyOrdersExistQuery,
  useGetCompanyOrderQuery,
  useCreateCompanyOrderMutation,
} = companyApi
